import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem,
  Paper, Select, Switch, Tab, Tabs, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  productService, productionOrderService, staffService,
  customerOrderService, stockItemService, additionalProcessService,
  setupParameterHistoryService,
} from '../services/api';
import api from '../services/api';

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('uk-UA', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function toIso(d) {
  if (!d) return '';
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth()+1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

function defaultPermol() {
  return { raw_material_id: '', quantity_kg: '', mode: 'kg', pct: '' };
}

function parsePermolKg(permol, totalKg) {
  if (!permol?.raw_material_id) return 0;
  if (permol.mode === 'pct') {
    const p = parseFloat(String(permol.pct ?? '').replace(',', '.'));
    return (Number.isFinite(p) && p > 0) ? (totalKg * Math.min(p, 100)) / 100 : 0;
  }
  const k = parseFloat(String(permol.quantity_kg ?? '').replace(',', '.'));
  return (Number.isFinite(k) && k > 0) ? k : 0;
}

const WAREHOUSES = [
  'Склад напівфабрикатів', 'Склад для пакування', 'Склад готової продукції',
  'Склад сировини', 'Склад упаковки', 'Склад барвників', 'Склад техматеріалів',
];

function emptyForm() {
  return {
    customer_order: '', customer_order_line: '', product: '', quantity: '',
    config_id: '', planned_start: '', comment: '', materials: [],
    packaging_rows: [], permol: defaultPermol(),
    destination_warehouse: 'Склад готової продукції', assigned_operator: '',
    needs_drying: false, drying_time_minutes: '',
  };
}

// ─── PlanOrderDialog ──────────────────────────────────────────────────────────

/**
 * Props:
 *   open          – boolean
 *   onClose()     – called on cancel/close
 *   onSaved()     – called after successful save
 *   editingOrder  – full order object when editing; null for new
 *   coLine        – pre-fill from customer order line { id, co_id, product, quantity, product_name, ... }
 *   products      – array from productService.getAll()
 */
export default function PlanOrderDialog({ open, onClose, onSaved, editingOrder, coLine, products }) {
  // ── local state ──────────────────────────────────────────────────────────
  const [tab, setTab] = useState(coLine ? 1 : 0);
  const [form, setForm] = useState(emptyForm);
  const [availability, setAvailability] = useState([]);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [packagingStock, setPackagingStock] = useState([]);
  const [stockItemsAll, setStockItemsAll] = useState([]);
  const [permolStockList, setPermolStockList] = useState([]);
  const [permolDetailOpen, setPermolDetailOpen] = useState(null);
  const [machineOperators, setMachineOperators] = useState([]);
  const [machineOperatorsLoading, setMachineOperatorsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [selectedProcessIds, setSelectedProcessIds] = useState([]);
  const [freshProductsMap, setFreshProductsMap] = useState({});
  const [coProductConfigs, setCoProductConfigs] = useState([]);
  const [coProducedAssignments, setCoProducedAssignments] = useState({});
  const [prodWarehouseStock, setProdWarehouseStock] = useState([]);
  const [transferQtys, setTransferQtys] = useState({});

  // Auto-fill transferQtys from quantity_required when materials / packaging change
  useEffect(() => {
    if (!open) return;
    const virginMats = (form.materials || []).filter((m) => !m.is_permolog);
    const packAtMachine = (form.packaging_rows || []).filter(
      (p) => p.pack_at_machine !== false && (p.stock_item || p._pm_id) && Number(p.quantity) > 0
    );
    if (virginMats.length === 0 && packAtMachine.length === 0) return;
    setTransferQtys((prev) => {
      const next = { ...prev };
      virginMats.forEach((m) => {
        const k = m.raw_material ? `r${m.raw_material}` : `d${m.dye}`;
        const qty = typeof m.quantity_required === 'number' ? m.quantity_required : Number(m.quantity_required);
        if (qty > 0) next[k] = String(Math.round(qty * 1000) / 1000);
      });
      packAtMachine.forEach((p) => {
        const pm = p.packaging_material || p._pm_id || p.stock_item;
        const k = `p${pm}`;
        const qty = Number(p.quantity) || 0;
        if (qty > 0) next[k] = String(Math.round(qty * 1000) / 1000);
      });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.materials, form.packaging_rows]);
  const [transferRequesterName, setTransferRequesterName] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem('currentUser') || 'null'); return u?.full_name || u?.username || ''; } catch { return ''; }
  });
  const [creatingRequests, setCreatingRequests] = useState(false);
  /** Реальна вага з останньої наладки (кг); null = немає історії → техкарта */
  const [historyWeightKg, setHistoryWeightKg] = useState(null);
  const [historyWeightMeta, setHistoryWeightMeta] = useState(null);

  // ── helpers ──────────────────────────────────────────────────────────────
  const loadOpsForMachine = useCallback((machineId) => {
    if (!machineId) { setMachineOperators([]); return; }
    setMachineOperatorsLoading(true);
    staffService.getAll({ machine: machineId, page_size: 200 })
      .then((r) => setMachineOperators(r.data?.results || r.data || []))
      .catch(() => {})
      .finally(() => setMachineOperatorsLoading(false));
  }, []);

  const getFreshProduct = useCallback(async (productId) => {
    if (!productId) return null;
    const id = Number(productId);
    if (freshProductsMap[id]) return freshProductsMap[id];
    try {
      const res = await productService.getById(id);
      const prod = res.data;
      setFreshProductsMap((m) => ({ ...m, [id]: prod }));
      return prod;
    } catch { return (products || []).find((p) => p.id === id) || null; }
  }, [freshProductsMap, products]);

  const loadAvailability = useCallback(async (productId) => {
    if (!productId) { setAvailability([]); return []; }
    try {
      const res = await productService.getAvailability(productId, new Date().toISOString());
      const slots = res.data?.results || res.data || [];
      setAvailability(slots);
      return slots;
    } catch { setAvailability([]); return []; }
  }, []);

  // ── init / reset when opened ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setErrorMsg('');
    setChangeReason('');

    // Load additional processes for the current product (or all if no product yet)
    const currentProductId = editingOrder?.product || null;
    const processParams = { is_active: true, page_size: 200 };
    if (currentProductId) processParams.product = currentProductId;
    additionalProcessService.getAll(processParams)
      .then((r) => setAvailableProcesses(r.data?.results || r.data || []))
      .catch(() => {});
    if (editingOrder) {
      setSelectedProcessIds((editingOrder.additional_processes || []).map(p => p.process));
    } else {
      setSelectedProcessIds([]);
    }
    customerOrderService.getAll({ page_size: 500 })
      .then((r) => setCustomerOrders(r.data?.results || r.data || []))
      .catch(() => {});
    api.get('/production-warehouse/stock/?page_size=1000')
      .then((r) => setProdWarehouseStock(r.data?.results || r.data || []))
      .catch(() => setProdWarehouseStock([]));
    stockItemService.getAll({ category: 'packaging', page_size: 500 })
      .then(async (r) => {
        const stock = r.data?.results || r.data || [];
        setPackagingStock(stock);
        setForm((f) => {
          if (!f.product) return f;
          // If rows already have all stock_items resolved — just update quantities/names from fresh stock
          const rows = f.packaging_rows || [];
          if (rows.length === 0) return f;
          // Resolve any unresolved rows using _pm_id
          const updatedRows = rows.map((row) => {
            if (row.stock_item != null && row.stock_item !== '') {
              // Підтягнути назву, якщо поле порожнє
              if (row.name) return row;
              const si = stock.find((s) => Number(s.id) === Number(row.stock_item));
              return si
                ? { ...row, name: si.packaging_material_name || row.name, unit: si.unit || row.unit, packaging_material: si.packaging_material }
                : row;
            }
            if (!row._pm_id && !row.packaging_material) return row;
            const pmId = Number(row._pm_id || row.packaging_material);
            const si = stock.find((s) => Number(s.packaging_material) === pmId);
            if (!si) return row;
            return {
              ...row,
              stock_item: si.id,
              packaging_material: si.packaging_material,
              name: si.packaging_material_name || row.name,
              unit: si.unit || row.unit,
            };
          });
          return { ...f, packaging_rows: updatedRows };
        });
      })
      .catch(() => {});
    stockItemService.getAll({ page_size: 1000 })
      .then((r) => setStockItemsAll(r.data?.results || r.data || []))
      .catch(() => {});

    if (editingOrder) {
      // Edit mode
      setEditingOrderId(editingOrder.id);
      setTab(1);
      loadAvailability(editingOrder.product).then((slots) => {
        const configId = editingOrder.manufacturing_config ||
          slots.find((s) => s.press_form_id === editingOrder.press_form && s.machine_id === editingOrder.machine)?.config_id || '';
        setForm({
          customer_order: editingOrder.customer_order || '',
          customer_order_line: editingOrder.customer_order_line || '',
          product: String(editingOrder.product),
          quantity: String(editingOrder.quantity),
          config_id: configId,
          planned_start: editingOrder.planned_start ? editingOrder.planned_start.slice(0, 16) : '',
          comment: editingOrder.comment || '',
          materials: (editingOrder.order_materials || []).map((m) => ({
            type: m.raw_material ? 'raw_material' : 'dye',
            raw_material: m.raw_material || null, raw_material_name: m.raw_material_name || '',
            dye: m.dye || null, dye_name: m.dye_name || '',
            quantity_required: String(m.quantity_required || ''),
            needs_drying: false,
            drying_time_minutes: '',
            is_permolog: Boolean(m.is_permolog),
          })),
          packaging_rows: (editingOrder.packaging_rows || []).map((r) => ({
            stock_item: r.stock_item != null && r.stock_item !== '' ? Number(r.stock_item) : null,
            packaging_material: r.packaging_material != null ? Number(r.packaging_material) : null,
            _pm_id: r.packaging_material != null ? Number(r.packaging_material) : null,
            quantity: String(r.quantity || ''),
            units_to_pack: '', pack_at_machine: r.pack_at_machine !== false,
            name: r.name || '', unit: r.unit || '',
          })),
          permol: defaultPermol(),
          destination_warehouse: editingOrder.destination_warehouse || 'Склад готової продукції',
          assigned_operator: editingOrder.assigned_operator || '',
          needs_drying: Boolean(editingOrder.needs_drying),
          drying_time_minutes: editingOrder.drying_time_minutes != null ? String(editingOrder.drying_time_minutes) : '',
        });
        if (configId) {
          const slot = slots.find((s) => s.config_id === configId);
          if (slot?.machine_id) {
            loadOpsForMachine(slot.machine_id);
            loadCoProductConfigs(slot.press_form_id, slot.machine_id, editingOrder.product, editingOrder.customer_order);
          }
          // Відновити призначення супутніх з order.co_produced
          if ((editingOrder.co_produced || []).length) {
            const asg = {};
            for (const c of editingOrder.co_produced) {
              asg[c.product] = c.customer_order_line || null;
            }
            setCoProducedAssignments(asg);
          }
        }
      });
    } else if (coLine) {
      // Pre-fill from CO line
      setEditingOrderId(null);
      setTab(1);
      setForm({ ...emptyForm(), customer_order: coLine.co_id || '', customer_order_line: coLine.id || '',
        product: String(coLine.product || ''), quantity: String(coLine.quantity || '') });
      loadAvailability(coLine.product).then((slots) => {
        const first = slots[0];
        if (first) {
          setForm((f) => ({ ...f, config_id: first.config_id, planned_start: toIso(first.free_from) || '' }));
          loadOpsForMachine(first.machine_id);
          loadCoProductConfigs(first.press_form_id, first.machine_id, coLine.product, coLine.co_id);
        }
      });
      // Load fresh product data and build packaging rows (з кастомним пакуванням замовника)
      handleProductChange(String(coLine.product), coLine.customer_id || coLine.customer || null);
    } else {
      setEditingOrderId(null);
      setTab(0);
      setForm(emptyForm());
      setAvailability([]);
      setMachineOperators([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── permol stock ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !form.product) { setPermolStockList([]); return; }
    let cancelled = false;
    productService.getPermolStock(form.product)
      .then((r) => { if (!cancelled) setPermolStockList(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setPermolStockList([]); });
    return () => { cancelled = true; };
  }, [open, form.product]);

  // ── історична вага з наладки для обраної конфігурації ─────────────────────
  useEffect(() => {
    if (!open || !form.config_id) {
      setHistoryWeightKg(null);
      setHistoryWeightMeta(null);
      return;
    }
    let cancelled = false;
    setupParameterHistoryService.latest({ manufacturing_config: form.config_id })
      .then((r) => {
        if (cancelled) return;
        const d = r.data;
        if (d?.found && d.actual_product_weight_kg != null && Number(d.actual_product_weight_kg) > 0) {
          setHistoryWeightKg(Number(d.actual_product_weight_kg));
          setHistoryWeightMeta(d);
        } else {
          setHistoryWeightKg(null);
          setHistoryWeightMeta(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryWeightKg(null);
          setHistoryWeightMeta(null);
        }
      });
    return () => { cancelled = true; };
  }, [open, form.config_id]);

  // ── resolve packaging stock_items when packagingStock loads ──────────────────
  useEffect(() => {
    if (!open || !packagingStock.length) return;
    setForm((f) => {
      const rows = f.packaging_rows || [];
      if (!rows.length) return f;
      const needsResolve = rows.some((r) => r.stock_item == null && r._pm_id);
      if (!needsResolve) return f;
      let changed = false;
      const updated = rows.map((row) => {
        let next = row;
        if ((row.stock_item == null || row.stock_item === '') && (row._pm_id || row.packaging_material)) {
          const pmId = Number(row._pm_id || row.packaging_material);
          const si = packagingStock.find((s) => Number(s.packaging_material) === pmId);
          if (si) {
            changed = true;
            next = {
              ...row,
              stock_item: si.id,
              packaging_material: si.packaging_material,
              name: si.packaging_material_name || row.name,
              unit: si.unit || row.unit,
            };
          }
        } else if (row.stock_item != null && row.stock_item !== '' && !row.name) {
          const si = packagingStock.find((s) => Number(s.id) === Number(row.stock_item));
          if (si) {
            changed = true;
            next = {
              ...row,
              name: si.packaging_material_name || '',
              unit: si.unit || row.unit,
              packaging_material: si.packaging_material ?? row.packaging_material,
              _pm_id: si.packaging_material ?? row._pm_id,
            };
          }
        }
        return next;
      });
      return changed ? { ...f, packaging_rows: updated } : f;
    });
  }, [open, packagingStock, form.packaging_rows]);

  // ── auto-calculate materials ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setForm((f) => {
      if (!f.product || !f.quantity) return f.materials?.length ? { ...f, materials: [] } : f;
      const product = freshProductsMap[Number(f.product)] || (products || []).find((p) => p.id === Number(f.product));
      if (!product) return f;
      const qty = Number(f.quantity);
      const techWeight = Number(product.product_weight) || 0;
      // Вага з наладки лише якщо реалістична відносно техкарти (інакше 102 кг замість 0.0116)
      const histOk = historyWeightKg != null && historyWeightKg > 0
        && (techWeight <= 0 || historyWeightKg <= techWeight * 5);
      const unitWeight = histOk ? historyWeightKg : techWeight;
      const totalKg = unitWeight * qty;
      if (totalKg <= 0) return f.materials?.length ? { ...f, materials: [] } : f;
      const permol = f.permol || defaultPermol();
      const rmId = permol.raw_material_id ? Number(permol.raw_material_id) : null;
      const stockRow = rmId ? permolStockList.find((s) => Number(s.raw_material_id) === rmId) : null;
      let P = parsePermolKg(permol, totalKg);
      if (rmId && stockRow) P = Math.min(P, Number(stockRow.available), totalKg);
      else P = 0;
      const prevMat = f.materials || [];
      const dryingByKey = new Map();
      prevMat.forEach((row) => {
        if (row.is_permolog) return;
        const k = row.raw_material ? `r${row.raw_material}` : row.dye ? `d${row.dye}` : '';
        if (k) dryingByKey.set(k, { needs_drying: Boolean(row.needs_drying), drying_time_minutes: row.drying_time_minutes ?? '' });
      });
      const newRows = [];
      (product.raw_material_components || []).forEach((c) => {
        const rm = c.raw_material?.id ?? c.raw_material;
        const pct = Number(c.percentage) || 0;
        if (!rm || pct <= 0) return;
        const virginQty = Math.max(0, (pct / 100) * totalKg - P * (pct / 100));
        if (virginQty < 0.0005) return;
        const d = dryingByKey.get(`r${rm}`) || {};
        newRows.push({ type: 'raw_material', raw_material: rm, raw_material_name: c.raw_material_name || c.raw_material?.name || '',
          dye: null, dye_name: '', quantity_required: virginQty,
          needs_drying: d.needs_drying ?? false, drying_time_minutes: d.drying_time_minutes ?? '', is_permolog: false });
      });
      (product.dye_components || []).forEach((c) => {
        const dy = c.dye?.id ?? c.dye;
        const pct = Number(c.percentage) || 0;
        if (!dy || pct <= 0) return;
        const virginQty = Math.max(0, (pct / 100) * totalKg - P * (pct / 100));
        if (virginQty < 0.0005) return;
        const d = dryingByKey.get(`d${dy}`) || {};
        newRows.push({ type: 'dye', raw_material: null, raw_material_name: '', dye: dy, dye_name: c.dye_name || c.dye?.name || '',
          quantity_required: virginQty, needs_drying: d.needs_drying ?? false, drying_time_minutes: d.drying_time_minutes ?? '', is_permolog: false });
      });
      if (P >= 0.01 && rmId && stockRow) {
        newRows.push({ type: 'raw_material', raw_material: rmId, raw_material_name: stockRow.name || 'Перемол',
          article: stockRow.article, dye: null, dye_name: '', quantity_required: P,
          needs_drying: false, drying_time_minutes: '', is_permolog: true });
      }

      // Повний цикл форми: матеріали супутніх гнізд (cavity_groups)
      if (coProductConfigs.length > 0) {
        const totalCav = Number(coProductConfigs[0]?.press_form_total_cavities) || 0;
        const siblingCav = coProductConfigs.reduce((s, c) => s + (Number(c.number_of_cavities) || 0), 0);
        const mainCav = Math.max(1, totalCav > siblingCav ? totalCav - siblingCav : (siblingCav || 1));
        let cycles = Math.ceil(qty / mainCav);
        // Урахувати qty привʼязаних ліній
        for (const cfg of coProductConfigs) {
          const lineId = coProducedAssignments[cfg.product];
          if (!lineId) continue;
          let lineQty = 0;
          for (const o of customerOrders) {
            const ln = (o.lines || []).find((l) => Number(l.id) === Number(lineId));
            if (ln) { lineQty = Number(ln.quantity) || 0; break; }
          }
          const cav = Number(cfg.number_of_cavities) || 1;
          if (lineQty > 0) cycles = Math.max(cycles, Math.ceil(lineQty / cav));
        }
        const mergeMat = (type, id, name, addQty) => {
          if (addQty < 0.0005) return;
          const idx = newRows.findIndex((r) => !r.is_permolog && (
            type === 'raw_material' ? Number(r.raw_material) === Number(id) : Number(r.dye) === Number(id)
          ));
          if (idx >= 0) {
            newRows[idx] = { ...newRows[idx], quantity_required: Number(newRows[idx].quantity_required) + addQty };
          } else if (type === 'raw_material') {
            newRows.push({
              type: 'raw_material', raw_material: id, raw_material_name: name,
              dye: null, dye_name: '', quantity_required: addQty,
              needs_drying: false, drying_time_minutes: '', is_permolog: false, is_co_product: true,
            });
          } else {
            newRows.push({
              type: 'dye', raw_material: null, raw_material_name: '', dye: id, dye_name: name,
              quantity_required: addQty, needs_drying: false, drying_time_minutes: '', is_permolog: false, is_co_product: true,
            });
          }
        };
        for (const cfg of coProductConfigs) {
          const byQty = cycles * (Number(cfg.number_of_cavities) || 0);
          const byProduct = freshProductsMap[Number(cfg.product)]
            || (products || []).find((p) => Number(p.id) === Number(cfg.product));
          const weight = Number(byProduct?.product_weight ?? cfg.product_weight) || 0;
          const byKg = weight * byQty;
          if (byKg <= 0) continue;
          (byProduct?.raw_material_components || []).forEach((c) => {
            const rm = c.raw_material?.id ?? c.raw_material;
            const pct = Number(c.percentage) || 0;
            if (!rm || pct <= 0) return;
            mergeMat('raw_material', rm, c.raw_material_name || c.raw_material?.name || '', (pct / 100) * byKg);
          });
          (byProduct?.dye_components || []).forEach((c) => {
            const dy = c.dye?.id ?? c.dye;
            const pct = Number(c.percentage) || 0;
            if (!dy || pct <= 0) return;
            mergeMat('dye', dy, c.dye_name || c.dye?.name || '', (pct / 100) * byKg);
          });
        }
      }

      return { ...f, materials: newRows };
    });
  }, [open, form.product, form.quantity, form.permol?.raw_material_id, form.permol?.quantity_kg, form.permol?.mode, form.permol?.pct, products, permolStockList, freshProductsMap, historyWeightKg, coProductConfigs, coProducedAssignments, customerOrders]);

  // Підвантажити техкарти супутніх виробів для розрахунку матеріалів
  useEffect(() => {
    if (!open || !coProductConfigs.length) return;
    coProductConfigs.forEach((c) => { getFreshProduct(c.product); });
  }, [open, coProductConfigs, getFreshProduct]);

  // Load additional processes filtered by selected product
  const loadProcessesForProduct = useCallback((productId) => {
    const params = { is_active: true, page_size: 200 };
    if (productId) params.product = productId;
    additionalProcessService.getAll(params)
      .then((r) => setAvailableProcesses(r.data?.results || r.data || []))
      .catch(() => {});
  }, []);

  // ── handlers ──────────────────────────────────────────────────────────────
  const buildPackagingRowsFromComps = useCallback((comps, qty, stock) => {
    return comps.map((comp) => {
      const pmId = Number(comp.packaging_material);
      const si = (stock || []).find((s) => Number(s.packaging_material) === pmId);
      const orderQtyNum = Number(qty) || 0;
      const autoQty = comp.quantity > 0 ? Math.ceil(orderQtyNum / Number(comp.quantity)) : 0;
      return {
        _pm_id: pmId,
        packaging_material: pmId,
        stock_item: si ? Number(si.id) : null,
        name: si?.packaging_material_name || comp.packaging_material_name || '',
        unit: si?.unit || 'шт',
        quantity: String(autoQty),
        units_to_pack: String(orderQtyNum),
        pack_at_machine: true,
      };
    });
  }, []);

  const buildPackagingRows = useCallback((productId, qty, currentPackStock) => {
    const prod = (freshProductsMap[Number(productId)] || null) ||
      (products || []).find((p) => p.id === Number(productId));
    const comps = prod?.packaging_components || [];
    if (comps.length === 0) return [];
    return buildPackagingRowsFromComps(comps, qty, currentPackStock || packagingStock);
  }, [freshProductsMap, products, packagingStock, buildPackagingRowsFromComps]);

  // Завантажує кастомне пакування замовника і повертає comps-like масив або null
  const loadCustomerPackagingComps = useCallback(async (productId, customerId) => {
    if (!productId || !customerId) return null;
    try {
      const res = await api.get('/customer-product-packaging/', {
        params: { product: productId, customer: customerId, page_size: 100 },
      });
      const rows = res.data?.results || res.data || [];
      if (!rows.length) return null;
      return rows.map(r => ({
        packaging_material: r.packaging_material,
        packaging_material_name: r.packaging_material_name,
        quantity: r.quantity,
      }));
    } catch { return null; }
  }, []);

  const loadCoProductConfigs = useCallback(async (pressFormId, machineId, productId, customerOrderId) => {
    if (!pressFormId || !machineId) { setCoProductConfigs([]); setCoProducedAssignments({}); return; }
    try {
      const res = await productionOrderService.getCoProductConfigs(pressFormId, machineId, productId);
      const cfgs = (res.data || []).filter((c) => Number(c.product) !== Number(productId));
      setCoProductConfigs(cfgs);
      // Авто-привʼязка рядків того ж CO
      const auto = {};
      for (const c of cfgs) {
        let lineId = null;
        if (customerOrderId) {
          const co = customerOrders.find((o) => Number(o.id) === Number(customerOrderId));
          const ln = (co?.lines || []).find(
            (l) => Number(l.product) === Number(c.product) && !l.production_order && !l.co_produced_by_order
          );
          if (ln) lineId = ln.id;
        }
        auto[c.product] = lineId;
      }
      setCoProducedAssignments(auto);
    } catch { setCoProductConfigs([]); }
  }, [customerOrders]);

  const handleProductChange = useCallback(async (productId, customerId, customerOrderId) => {
    const freshProd = await getFreshProduct(productId);
    // Визначаємо які comps використовувати: кастомні для замовника або стандартні
    let comps = freshProd?.packaging_components || [];
    if (customerId) {
      const custComps = await loadCustomerPackagingComps(productId, customerId);
      if (custComps) comps = custComps;
    }
    const coId = customerOrderId != null ? customerOrderId : form.customer_order;
    setForm((f) => {
      const rows = buildPackagingRowsFromComps(comps, f.quantity, packagingStock);
      return { ...f, product: productId, config_id: '', planned_start: '', permol: defaultPermol(), packaging_rows: rows };
    });
    loadProcessesForProduct(productId);
    setSelectedProcessIds([]);
    const slots = await loadAvailability(productId);
    const first = slots[0];
    if (first) {
      setForm((f) => ({ ...f, config_id: first.config_id, planned_start: toIso(first.free_from) || '', assigned_operator: '' }));
      loadOpsForMachine(first.machine_id);
      loadCoProductConfigs(first.press_form_id, first.machine_id, productId, coId);
    }
  }, [loadAvailability, loadOpsForMachine, loadProcessesForProduct, buildPackagingRowsFromComps, packagingStock, getFreshProduct, loadCustomerPackagingComps, loadCoProductConfigs, form.customer_order]);

  const handleSlotChange = useCallback((configId) => {
    const slot = availability.find((a) => a.config_id === configId);
    setForm((f) => ({ ...f, config_id: configId, planned_start: slot ? toIso(slot.free_from) : f.planned_start, assigned_operator: '' }));
    loadOpsForMachine(slot?.machine_id);
    if (slot) loadCoProductConfigs(slot.press_form_id, slot.machine_id, form.product, form.customer_order);
  }, [availability, loadOpsForMachine, loadCoProductConfigs, form.product, form.customer_order]);

  const customerOrderLineOptions = useMemo(() => {
    const out = [];
    for (const o of customerOrders) {
      for (const ln of o.lines || []) {
        out.push({ lineId: ln.id, orderId: o.id, product: ln.product, quantity: ln.quantity,
          label: `#${o.number} — ${ln.product_name || '?'} × ${ln.quantity ?? ''}`, order: o });
      }
    }
    return out;
  }, [customerOrders]);

  const customerOrdersForTab = useMemo(() => {
    const rows = [];
    for (const o of customerOrders) {
      for (const ln of o.lines || []) {
        if (ln.co_produced_by_order) continue;
        if (!ln.production_order) rows.push({ ...ln, order: o, plannerKey: `${o.id}-${ln.id}` });
      }
    }
    return rows;
  }, [customerOrders]);

  const handleTakeOrder = async (row) => {
    const co = row.order;
    setForm((f) => ({ ...f, customer_order: co.id, customer_order_line: row.id,
      product: row.product || '', quantity: row.quantity != null ? String(row.quantity) : '',
      comment: co.comment || '',
      destination_warehouse: (co.warehouse && String(co.warehouse).trim()) || 'Склад готової продукції',
      permol: defaultPermol() }));
    setTab(1);
    if (row.product) await handleProductChange(row.product, co.customer || null, co.id);
  };

  const handleSubmit = async () => {
    const slot = availability.find((a) => a.config_id === form.config_id);
    if (!form.product) { setErrorMsg('Оберіть виріб.'); return; }
    if (!form.quantity || form.quantity < 1) { setErrorMsg('Вкажіть кількість.'); return; }
    if (!slot || !form.config_id) { setErrorMsg('Оберіть пару «Станок + Прес-форма».'); return; }
    if (!form.planned_start) { setErrorMsg('Вкажіть дату та час початку.'); return; }
    const destWh = String(form.destination_warehouse || '').trim();
    if (!destWh) { setErrorMsg("Оберіть склад надходження."); return; }
    const materialsPayload = (form.materials || [])
      .filter((m) => (m.raw_material || m.dye) && Number(m.quantity_required) > 0)
      .map((m) => ({
        raw_material: m.raw_material || undefined,
        dye: m.dye || undefined,
        quantity_required: Number(m.quantity_required),
        needs_drying: false,
        drying_time_minutes: null,
      }));
    if (materialsPayload.length === 0) { setErrorMsg('Немає матеріалів. Перевірте техкарту та вагу виробу.'); return; }
    if (form.needs_drying && (!form.drying_time_minutes || Number(form.drying_time_minutes) <= 0)) {
      setErrorMsg('Вкажіть час сушіння замісу (хв).'); return;
    }
    if (editingOrderId && !changeReason.trim()) { setErrorMsg('Вкажіть причину змін — це обовʼязкове поле при редагуванні.'); return; }
    const packRows = (form.packaging_rows || []).filter((p) => p.stock_item && Number(p.quantity) > 0);
    const packagingNote = packRows.map((p) => {
      const item = packagingStock.find((s) => Number(s.id) === Number(p.stock_item));
      const name = item?.packaging_material_name || p.name || 'Упаковка';
      const mode = p.pack_at_machine === false ? '→ пакування' : 'ТПА';
      return `${mode}: ${name}: ${Number(p.quantity)}${item?.unit ? ` ${item.unit}` : ''}`;
    }).join('; ');
    const needsPackaging = packRows.some((p) => p.pack_at_machine === false);
    const finalDest = needsPackaging ? 'Склад для пакування' : destWh;
    const payload = {
      product: Number(form.product), quantity: Number(form.quantity),
      press_form: slot.press_form_id, machine: slot.machine_id,
      planned_start: form.planned_start.length === 16 ? `${form.planned_start}:00` : form.planned_start,
      comment: form.comment || '', change_reason: changeReason.trim(), packaging_note: packagingNote,
      packaging_rows: packRows.map((p) => {
        const item = packagingStock.find((s) => Number(s.id) === Number(p.stock_item));
        const pm = Number(p.packaging_material || p._pm_id || item?.packaging_material) || null;
        return {
          stock_item: Number(p.stock_item),
          packaging_material: pm,
          name: item?.packaging_material_name || p.name || '',
          unit: item?.unit || p.unit || 'шт',
          quantity: Number(p.quantity),
          pack_at_machine: p.pack_at_machine !== false,
        };
      }),
      destination_warehouse: finalDest, materials: materialsPayload,
      needs_drying: Boolean(form.needs_drying),
      drying_time_minutes: form.needs_drying ? Number(form.drying_time_minutes) : null,
      customer_order: form.customer_order || null, customer_order_line: form.customer_order_line || null,
      co_produced_assignments: Object.entries(coProducedAssignments).map(([productId, lineId]) => ({
        product: Number(productId),
        customer_order_line: lineId || null,
      })),
    };
    setSaving(true); setErrorMsg('');
    try {
      let orderId;
      if (editingOrderId) {
        await productionOrderService.update(editingOrderId, payload);
        orderId = editingOrderId;
      } else {
        const res = await productionOrderService.create(payload);
        orderId = res.data?.id;
      }
      if (form.assigned_operator && orderId) {
        await productionOrderService.assignOperator(orderId, form.assigned_operator).catch(() => {});
      }
      if (orderId && availableProcesses.length > 0) {
        await productionOrderService.setAdditionalProcesses(orderId, { process_ids: selectedProcessIds }).catch(() => {});
      }
      onSaved?.();
      onClose();
    } catch (e) {
      const data = e.response?.data;
      const matErr = Array.isArray(data?.materials) ? data.materials[0] : data?.materials;
      if (typeof matErr === 'string') setErrorMsg(matErr);
      else if (data?.destination_warehouse) setErrorMsg(Array.isArray(data.destination_warehouse) ? data.destination_warehouse[0] : String(data.destination_warehouse));
      else setErrorMsg(data?.detail || e.message || 'Помилка збереження.');
    } finally { setSaving(false); }
  };

  // ── render ────────────────────────────────────────────────────────────────
  const planProduct = (form.product ? (freshProductsMap[Number(form.product)] || null) : null)
    || (products || []).find((p) => p.id === Number(form.product));
  const techWeightKg = Number(planProduct?.product_weight) || 0;
  const historyWeightOk = historyWeightKg != null && historyWeightKg > 0
    && (techWeightKg <= 0 || historyWeightKg <= techWeightKg * 5);
  const unitWeightKg = historyWeightOk ? historyWeightKg : techWeightKg;
  const totalKg = unitWeightKg * Number(form.quantity);
  const virginMaterials = (form.materials || []).filter((m) => !m.is_permolog);
  const matIdxFor = (row) => (form.materials || []).findIndex((r) => {
    if (r.is_permolog) return false;
    if (row.raw_material) return Number(r.raw_material) === Number(row.raw_material);
    if (row.dye) return Number(r.dye) === Number(row.dye);
    return false;
  });
  const prodPackComps = planProduct?.packaging_components || [];
  const compByPmId = {};
  prodPackComps.forEach((c) => { compByPmId[Number(c.packaging_material)] = { ...c, packaging_material: Number(c.packaging_material) }; });
  // Кастомне пакування / рядки з _pm_id також у мапі (інакше select порожній, а назва лише в алерті)
  (form.packaging_rows || []).forEach((r) => {
    const pm = Number(r.packaging_material || r._pm_id);
    if (pm && !compByPmId[pm]) {
      compByPmId[pm] = { packaging_material: pm, packaging_material_name: r.name || '', quantity: 1 };
    }
  });
  const filteredPackStock = Object.keys(compByPmId).length > 0
    ? packagingStock.filter((s) => s.packaging_material != null && compByPmId[Number(s.packaging_material)])
    : packagingStock;
  // Завжди показуємо вже обрані позиції в select (навіть якщо випали з фільтра)
  const selectedPackIds = new Set(
    (form.packaging_rows || []).map((r) => Number(r.stock_item)).filter((id) => Number.isFinite(id) && id > 0)
  );
  const packStockOptions = (() => {
    const byId = new Map(filteredPackStock.map((s) => [Number(s.id), s]));
    packagingStock.forEach((s) => {
      if (selectedPackIds.has(Number(s.id))) byId.set(Number(s.id), s);
    });
    return Array.from(byId.values());
  })();
  const packAtMachineRows = (form.packaging_rows || []).filter(
    (p) => p.pack_at_machine !== false && Number(p.quantity) > 0 && (p.stock_item || p._pm_id || p.packaging_material)
  );
  const orderQty = Number(form.quantity) || 0;
  const allocatedUnits = (form.packaging_rows || []).reduce((s, r) => s + (Number(r.units_to_pack) || 0), 0);
  const remainingUnits = orderQty - allocatedUnits;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>{editingOrderId ? 'Редагувати замовлення' : 'Запланувати замовлення'}</DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label="З замовлень" />
            <Tab label="Нове" />
          </Tabs>

          {/* ── Tab 0: customer orders ── */}
          {tab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Позиції замовлень клієнта без виробничого замовлення. Натисніть «Взяти в роботу», щоб підставити виріб і кількість у форму планування.
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Номер</TableCell><TableCell>Виріб</TableCell>
                      <TableCell>К-сть</TableCell><TableCell>Дата</TableCell>
                      <TableCell>Дедлайн</TableCell><TableCell align="right">Дія</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerOrdersForTab.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">Немає доступних замовлень</Typography>
                      </TableCell></TableRow>
                    ) : customerOrdersForTab.map((row) => (
                      <TableRow key={row.plannerKey} hover>
                        <TableCell>{row.order?.number || `#${row.order?.id}`}</TableCell>
                        <TableCell>{row.product_name || '—'}</TableCell>
                        <TableCell>{row.quantity ?? '—'}</TableCell>
                        <TableCell>{row.order?.order_date ? new Date(row.order.order_date).toLocaleDateString('uk-UA') : '—'}</TableCell>
                        <TableCell>{row.order?.deadline ? new Date(row.order.deadline).toLocaleDateString('uk-UA') : '—'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" onClick={() => handleTakeOrder(row)}>Взяти в роботу</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ── Tab 1: form ── */}
          {tab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {/* CO line */}
              <TextField select label="Позиція замовлення клієнта (опціонально)"
                value={form.customer_order_line || ''} fullWidth
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : '';
                  const opt = customerOrderLineOptions.find((x) => x.lineId === val);
                  setForm((f) => ({ ...f, customer_order_line: val || '', customer_order: opt ? opt.orderId : '',
                    product: opt?.product || f.product, quantity: opt?.quantity != null ? String(opt.quantity) : f.quantity,
                    comment: opt?.order?.comment || f.comment,
                    destination_warehouse: opt?.order ? ((opt.order.warehouse && String(opt.order.warehouse).trim()) || 'Склад готової продукції') : f.destination_warehouse || 'Склад готової продукції' }));
                  if (opt?.product) handleProductChange(opt.product, opt?.order?.customer || null, opt.orderId);
                }}>
                <MenuItem value="">— без привʼязки до замовлення —</MenuItem>
                {customerOrderLineOptions.map((opt) => (
                  <MenuItem key={opt.lineId} value={opt.lineId}>{opt.label}</MenuItem>
                ))}
              </TextField>

              {/* Product */}
              <TextField select label="Виріб" value={form.product} fullWidth onChange={(e) => handleProductChange(e.target.value)}>
                <MenuItem value="">— обрати —</MenuItem>
                {(products || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>

              {/* Quantity */}
              <TextField type="number" label="Кількість" value={form.quantity} fullWidth inputProps={{ min: 1 }}
                onChange={(e) => {
                  const newQty = e.target.value;
                  setForm((f) => {
                    // Recalculate packaging rows quantities when order qty changes
                    const prod = (products || []).find((p) => p.id === Number(f.product));
                    const comps = prod?.packaging_components || [];
                    const compByPm = {};
                    comps.forEach((c) => { compByPm[c.packaging_material] = c; });
                    const updatedRows = (f.packaging_rows || []).map((row) => {
                      const si = packagingStock.find((s) => s.id === row.stock_item);
                      const comp = si ? compByPm[si.packaging_material] : null;
                      if (!comp) return { ...row, quantity: row.quantity };
                      const autoQty = comp.quantity > 0 ? Math.ceil(Number(newQty) / comp.quantity) : 0;
                      return { ...row, units_to_pack: String(newQty), quantity: String(autoQty) };
                    });
                    return { ...f, quantity: newQty, packaging_rows: updatedRows };
                  });
                }} />

              {/* Warehouse */}
              <TextField select required label="Склад надходження готової продукції" value={form.destination_warehouse || ''} fullWidth
                helperText="Куди надійде випуск після завершення замовлення."
                onChange={(e) => setForm((f) => ({ ...f, destination_warehouse: e.target.value }))}>
                {WAREHOUSES.map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
              </TextField>

              {/* Machine + PressForm slot */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Станок + Прес-форма (коли звільняться)</Typography>
                {!form.product ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    Спочатку оберіть виріб — зʼявиться список пар станок/прес-форма з часом звільнення.
                  </Typography>
                ) : availability.length === 0 ? (
                  <Typography variant="body2" color="warning.main" sx={{ py: 1 }}>
                    Для цього виробу немає пар станок/прес-форма. Додайте конфігурацію виготовлення у розділі Вироби.
                  </Typography>
                ) : (
                  <TextField select label="Оберіть пару" value={form.config_id || ''} fullWidth size="small"
                    onChange={(e) => handleSlotChange(e.target.value ? Number(e.target.value) : '')}>
                    <MenuItem value="">— обрати пару —</MenuItem>
                    {availability.map((slot) => (
                      <MenuItem key={slot.config_id} value={slot.config_id}>
                        {slot.machine_identifier} / {slot.press_form_identifier} — звільняться з {formatDt(slot.free_from)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>

              {/* Operator */}
              {form.config_id && (
                <FormControl fullWidth>
                  <InputLabel>Оператор</InputLabel>
                  <Select label="Оператор" value={form.assigned_operator}
                    onChange={(e) => setForm((f) => ({ ...f, assigned_operator: e.target.value }))}>
                    <MenuItem value=""><em>— не призначено —</em></MenuItem>
                    {machineOperatorsLoading ? (
                      <MenuItem disabled><em>Завантаження...</em></MenuItem>
                    ) : machineOperators.map((op) => (
                      <MenuItem key={op.id} value={op.id}>{op.full_name}</MenuItem>
                    ))}
                  </Select>
                  {machineOperators.length === 0 && !machineOperatorsLoading && form.config_id && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      На цей ТПА не призначено жодного оператора
                    </Typography>
                  )}
                </FormControl>
              )}

              {/* Co-produced products (kit / multi-cavity) */}
              {coProductConfigs.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                    Комплект форми (супутні гнізда — один цикл)
                  </Typography>
                  {coProductConfigs.some((c) => !coProducedAssignments[c.product]) && (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 0.75 }}>
                      Увага: незамовлені супутні підуть на склад виробництва; сировина рахується на повний цикл.
                    </Typography>
                  )}
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    {coProductConfigs.map((cfg) => {
                      const lineOptions = [];
                      for (const o of customerOrders) {
                        for (const ln of o.lines || []) {
                          if (Number(ln.product) === Number(cfg.product) && !ln.production_order && !ln.co_produced_by_order) {
                            lineOptions.push({ lineId: ln.id, label: `Замовлення #${o.number} × ${ln.quantity}` });
                          }
                        }
                      }
                      return (
                        <Box key={cfg.product} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>{cfg.product_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {cfg.number_of_cavities} гн. з {cfg.press_form_total_cavities}
                            </Typography>
                          </Box>
                          <FormControl size="small" sx={{ minWidth: 240 }}>
                            <InputLabel>Прив'язати до замовлення</InputLabel>
                            <Select
                              label="Прив'язати до замовлення"
                              value={coProducedAssignments[cfg.product] ?? ''}
                              onChange={(e) => setCoProducedAssignments((prev) => ({ ...prev, [cfg.product]: e.target.value || null }))}
                            >
                              <MenuItem value=""><em>→ Склад виробництва</em></MenuItem>
                              {lineOptions.map((o) => <MenuItem key={o.lineId} value={o.lineId}>{o.label}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Box>
                      );
                    })}
                  </Paper>
                </Box>
              )}

              {/* Materials */}
              <Box>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                  Сировина та барвники (резервуються на складі, списуються після завершення)
                </Typography>
                {form.product && form.quantity && historyWeightKg != null && (
                  <Typography
                    variant="caption"
                    color={historyWeightOk ? 'primary.main' : 'warning.main'}
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {historyWeightOk
                      ? `Вага з наладки: ${Math.round(historyWeightKg * 1000)} г/шт`
                      : `Вага з наладки ${Math.round(historyWeightKg * 1000)} г/шт ігнорується (нереалістична)`}
                    {historyWeightMeta?.recorded_at
                      ? ` (${formatDt(historyWeightMeta.recorded_at)})`
                      : ''}
                    {techWeightKg > 0
                      ? ` · техкарта: ${Math.round(techWeightKg * 1000)} г`
                      : ''}
                  </Typography>
                )}
                {form.product && form.quantity ? (
                  totalKg <= 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                      У виробі не задана вага виробу (г) — неможливо порахувати сировину.
                    </Typography>
                  ) : virginMaterials.length === 0 && !(form.materials || []).some((m) => m.is_permolog) ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                      У виробі не заповнена техкарта (сировина та барвники з відсотками).
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Назва</TableCell>
                            <TableCell align="right">Потрібно (кг)</TableCell>
                            <TableCell align="right">На виробництві (кг)</TableCell>
                            <TableCell align="right">На складі (кг)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {virginMaterials.map((row) => {
                            const keyId = row.raw_material || row.dye;
                            const si = (stockItemsAll || []).find((s) => {
                              const rm = s.raw_material?.id ?? s.raw_material;
                              const dy = s.dye?.id ?? s.dye;
                              if (row.raw_material) return Number(rm) === Number(row.raw_material);
                              if (row.dye) return Number(dy) === Number(row.dye);
                              return false;
                            });
                            const availQty = si?.quantity != null ? Number(si.quantity) : null;
                            const prodWh = (prodWarehouseStock || []).find((s) => {
                              if (row.raw_material) return Number(s.raw_material) === Number(row.raw_material) || Number(s.raw_material_id) === Number(row.raw_material);
                              if (row.dye) return Number(s.dye) === Number(row.dye) || Number(s.dye_id) === Number(row.dye);
                              return false;
                            });
                            const prodWhQty = prodWh?.quantity != null ? Number(prodWh.quantity) : null;
                            return (
                              <TableRow key={keyId}>
                                <TableCell>{row.raw_material_name || row.dye_name || '—'}</TableCell>
                                <TableCell align="right">
                                  {(typeof row.quantity_required === 'number' ? row.quantity_required : Number(row.quantity_required)).toFixed(3)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: prodWhQty != null && prodWhQty > 0 ? 'success.main' : 'text.secondary' }}>
                                  {prodWhQty == null ? '—' : prodWhQty.toFixed(3)}
                                </TableCell>
                                <TableCell align="right">{availQty == null ? '—' : availQty.toFixed(3)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                    Оберіть виріб та кількість — кількість сировини підрахується автоматично.
                  </Typography>
                )}

                {/* Єдине сушіння замісу */}
                <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fafafa' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.needs_drying)}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          needs_drying: e.target.checked,
                          drying_time_minutes: e.target.checked ? (f.drying_time_minutes || '') : '',
                        }))}
                      />
                    }
                    label={<Typography variant="subtitle2">Потребує сушіння (заміс)</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Матеріали приходять уже змішаними — сушіння одне на весь заміс. Запускає оператор ТПА кнопкою «Почати роботу».
                  </Typography>
                  {form.needs_drying && (
                    <TextField
                      size="small"
                      type="number"
                      label="Час сушіння (хв)"
                      value={form.drying_time_minutes ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, drying_time_minutes: e.target.value }))}
                      inputProps={{ min: 1, step: 1 }}
                      sx={{ width: 200 }}
                      helperText={
                        form.planned_start && Number(form.drying_time_minutes) > 0
                          ? `Сушіння треба почати о ${(() => {
                              const ps = new Date(form.planned_start);
                              const startDry = new Date(ps.getTime() - Number(form.drying_time_minutes) * 60000);
                              return startDry.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
                            })()} (план старту мінус час сушіння)`
                          : ' '
                      }
                    />
                  )}
                </Box>

                {/* Permol */}
                {form.product && form.quantity && permolStockList.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Перемол (за наявності на складі сировини)</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Вкажіть кількість перемолу в кг або у % від загальної маси замовлення ({totalKg.toFixed(3)} кг). Потреба у новій сировині зменшиться пропорційно.
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width={48} /><TableCell>Назва</TableCell>
                            <TableCell>Артикул</TableCell><TableCell align="right">Доступно (кг)</TableCell>
                            <TableCell align="right">Склад</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {permolStockList.map((ps) => {
                            const selected = Number(form.permol?.raw_material_id) === Number(ps.raw_material_id);
                            return (
                              <TableRow key={ps.raw_material_id} selected={selected} hover sx={{ cursor: 'pointer' }}
                                onClick={() => setForm((f) => ({ ...f, permol: { ...(f.permol || defaultPermol()), raw_material_id: ps.raw_material_id } }))}>
                                <TableCell>
                                  <Checkbox size="small" checked={selected} onChange={(e) => {
                                    e.stopPropagation();
                                    setForm((f) => ({ ...f, permol: { ...(f.permol || defaultPermol()), raw_material_id: e.target.checked ? ps.raw_material_id : '' } }));
                                  }} />
                                </TableCell>
                                <TableCell>{ps.name}</TableCell>
                                <TableCell>{ps.article || '—'}</TableCell>
                                <TableCell align="right">{Number(ps.available).toFixed(3)}</TableCell>
                                <TableCell align="right">
                                  <Button size="small" onClick={(e) => { e.stopPropagation(); setPermolDetailOpen(ps); }}>Деталі</Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {Number(form.permol?.raw_material_id) > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <ToggleButtonGroup size="small" value={form.permol?.mode || 'kg'} exclusive
                          onChange={(e, v) => { if (v) setForm((f) => ({ ...f, permol: { ...(f.permol || defaultPermol()), mode: v } })); }}>
                          <ToggleButton value="kg">Кг</ToggleButton>
                          <ToggleButton value="pct">% від маси</ToggleButton>
                        </ToggleButtonGroup>
                        {form.permol?.mode === 'pct' ? (
                          <TextField size="small" label="% від загальної маси" type="number" value={form.permol?.pct ?? ''}
                            onChange={(e) => setForm((f) => ({ ...f, permol: { ...(f.permol || defaultPermol()), pct: e.target.value } }))}
                            inputProps={{ min: 0.01, max: 100, step: 0.1 }} sx={{ width: 220 }} />
                        ) : (
                          <TextField size="small" label="Кількість перемолу (кг)" type="number" value={form.permol?.quantity_kg ?? ''}
                            onChange={(e) => setForm((f) => ({ ...f, permol: { ...(f.permol || defaultPermol()), quantity_kg: e.target.value } }))}
                            inputProps={{ min: 0.01, step: 0.01 }} sx={{ width: 200 }} />
                        )}
                      </Box>
                    )}
                  </Box>
                )}
                {form.materials?.length > 0 && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    При збереженні замовлення ці матеріали будуть зарезервовані на складі.
                  </Typography>
                )}
              </Box>

              {/* Transfer request table: сировина + барвник + упаковка (біля ТПА) */}
              {(virginMaterials.length > 0 || packAtMachineRows.length > 0) && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                    Заявка на переміщення (зі складу на склад виробництва)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Упаковка з галочкою «Біля ТПА» додається третім рядком до тієї ж заявки.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Матеріал</TableCell>
                          <TableCell>Тип</TableCell>
                          <TableCell align="right">Потрібно</TableCell>
                          <TableCell align="right">Склад виробництва</TableCell>
                          <TableCell align="right">Основний склад</TableCell>
                          <TableCell align="right">К-сть для переміщення</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {virginMaterials.map((row) => {
                          const matKey = row.raw_material ? `r${row.raw_material}` : `d${row.dye}`;
                          const prodWh = (prodWarehouseStock || []).find((s) => {
                            if (row.raw_material) return Number(s.raw_material) === Number(row.raw_material) || Number(s.raw_material_id) === Number(row.raw_material);
                            if (row.dye) return Number(s.dye) === Number(row.dye) || Number(s.dye_id) === Number(row.dye);
                            return false;
                          });
                          const mainSi = (stockItemsAll || []).find((s) => {
                            if (row.raw_material) return Number(s.raw_material) === Number(row.raw_material);
                            if (row.dye) return Number(s.dye) === Number(row.dye);
                            return false;
                          });
                          const prodWhQty = prodWh?.quantity != null ? Number(prodWh.quantity) : 0;
                          const mainQty = mainSi?.quantity != null ? Number(mainSi.quantity) : 0;
                          const reqQty = Number(row.quantity_required);
                          const unit = 'кг';
                          return (
                            <TableRow key={matKey}>
                              <TableCell>{row.raw_material_name || row.dye_name || '—'}</TableCell>
                              <TableCell><Chip size="small" label={row.raw_material ? 'Сировина' : 'Барвник'} sx={{ height: 22, fontSize: '0.7rem' }} /></TableCell>
                              <TableCell align="right">{reqQty.toFixed(3)} {unit}</TableCell>
                              <TableCell align="right" sx={{ color: prodWhQty > 0 ? 'success.main' : 'text.secondary' }}>
                                {prodWhQty.toFixed(3)} {unit}
                              </TableCell>
                              <TableCell align="right">{mainQty.toFixed(3)} {unit}</TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small" type="number"
                                  value={transferQtys[matKey] ?? ''}
                                  onChange={(e) => setTransferQtys((prev) => ({ ...prev, [matKey]: e.target.value }))}
                                  inputProps={{ min: 0, step: 0.001 }}
                                  sx={{ width: 130 }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {packAtMachineRows.map((row, pIdx) => {
                          const pmId = Number(row.packaging_material || row._pm_id);
                          const stockItem = packStockOptions.find((x) => Number(x.id) === Number(row.stock_item))
                            || packagingStock.find((x) => Number(x.id) === Number(row.stock_item))
                            || packagingStock.find((x) => Number(x.packaging_material) === pmId);
                          const resolvedPm = Number(stockItem?.packaging_material || pmId);
                          const matKey = `p${resolvedPm || row.stock_item || pIdx}`;
                          const prodWh = (prodWarehouseStock || []).find((s) =>
                            Number(s.packaging_material) === resolvedPm || Number(s.packaging_material_id) === resolvedPm
                          );
                          const mainQty = stockItem?.quantity != null ? Number(stockItem.quantity) : 0;
                          const prodWhQty = prodWh?.quantity != null ? Number(prodWh.quantity) : 0;
                          const reqQty = Number(row.quantity) || 0;
                          const unit = stockItem?.unit || row.unit || 'шт';
                          const name = stockItem?.packaging_material_name || row.name || 'Упаковка';
                          return (
                            <TableRow key={matKey}>
                              <TableCell>{name}</TableCell>
                              <TableCell><Chip size="small" label="Упаковка" color="warning" sx={{ height: 22, fontSize: '0.7rem' }} /></TableCell>
                              <TableCell align="right">{reqQty} {unit}</TableCell>
                              <TableCell align="right" sx={{ color: prodWhQty > 0 ? 'success.main' : 'text.secondary' }}>
                                {prodWhQty} {unit}
                              </TableCell>
                              <TableCell align="right">{mainQty} {unit}</TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small" type="number"
                                  value={transferQtys[matKey] ?? ''}
                                  onChange={(e) => setTransferQtys((prev) => ({ ...prev, [matKey]: e.target.value }))}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{ width: 130 }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                    <TextField
                      size="small"
                      label="Хто замовляє"
                      value={transferRequesterName}
                      onChange={(e) => setTransferRequesterName(e.target.value)}
                      sx={{ display: 'none' }}
                    />
                    <Button
                      variant="outlined"
                      color="secondary"
                      disabled={creatingRequests || Object.values(transferQtys).every((v) => !v || Number(v) <= 0)}
                      onClick={async () => {
                        setCreatingRequests(true);
                        try {
                          const matRows = virginMaterials.filter((row) => {
                            const k = row.raw_material ? `r${row.raw_material}` : `d${row.dye}`;
                            return Number(transferQtys[k]) > 0;
                          });
                          const packRowsReq = packAtMachineRows.filter((row, pIdx) => {
                            const pmId = Number(row.packaging_material || row._pm_id);
                            const stockItem = packagingStock.find((x) => Number(x.id) === Number(row.stock_item))
                              || packagingStock.find((x) => Number(x.packaging_material) === pmId);
                            const resolvedPm = Number(stockItem?.packaging_material || pmId);
                            const k = `p${resolvedPm || row.stock_item || pIdx}`;
                            return Number(transferQtys[k]) > 0 && resolvedPm;
                          });
                          const posts = [
                            ...matRows.map((row) => {
                              const k = row.raw_material ? `r${row.raw_material}` : `d${row.dye}`;
                              return api.post('/stock-material-requests/', {
                                requester_name: transferRequesterName.trim(),
                                item_category: row.raw_material ? 'raw_material' : 'dye',
                                raw_material: row.raw_material || null,
                                dye: row.dye || null,
                                packaging_material: null,
                                consumable_material: null,
                                quantity: Number(transferQtys[k]),
                              });
                            }),
                            ...packRowsReq.map((row, pIdx) => {
                              const pmId = Number(row.packaging_material || row._pm_id);
                              const stockItem = packagingStock.find((x) => Number(x.id) === Number(row.stock_item))
                                || packagingStock.find((x) => Number(x.packaging_material) === pmId);
                              const resolvedPm = Number(stockItem?.packaging_material || pmId);
                              const k = `p${resolvedPm || row.stock_item || pIdx}`;
                              return api.post('/stock-material-requests/', {
                                requester_name: transferRequesterName.trim(),
                                item_category: 'packaging_material',
                                raw_material: null,
                                dye: null,
                                packaging_material: resolvedPm,
                                consumable_material: null,
                                quantity: Number(transferQtys[k]),
                                notes: 'Пакування біля ТПА → склад виробництва',
                              });
                            }),
                          ];
                          await Promise.all(posts);
                          setTransferQtys({});
                          alert(`Заявку створено (${posts.length} позицій)`);
                        } catch (e) {
                          alert('Помилка: ' + (e?.response?.data?.detail || e.message));
                        } finally {
                          setCreatingRequests(false);
                        }
                      }}
                    >
                      {creatingRequests ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                      Створити заявку
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Planned start */}
              <TextField label="Дата і час початку (план)" type="datetime-local" value={form.planned_start} fullWidth
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setForm((f) => ({ ...f, planned_start: e.target.value }))} />

              {/* Additional processes */}
              {availableProcesses.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Додаткові процеси</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {availableProcesses.map(p => {
                      const selected = selectedProcessIds.includes(p.id);
                      return (
                        <Chip
                          key={p.id}
                          label={`${p.icon || ''} ${p.name}`}
                          onClick={() => setSelectedProcessIds(ids =>
                            ids.includes(p.id) ? ids.filter(i => i !== p.id) : [...ids, p.id]
                          )}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          sx={selected ? { bgcolor: '#1565c0', color: '#fff' } : {}}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Comment */}
              <TextField label="Коментар" multiline value={form.comment} fullWidth
                onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} />

              {/* Change reason (edit mode only) */}
              {editingOrderId && (
                <TextField
                  label="Причина змін *"
                  multiline rows={2} fullWidth required
                  value={changeReason}
                  placeholder="Обовʼязково вкажіть причину внесених змін"
                  onChange={(e) => setChangeReason(e.target.value)}
                  sx={{ bgcolor: '#fff8e1', borderRadius: 1 }}
                />
              )}

              {/* Packaging */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2">Пакування (зі складу «Упаковка»)</Typography>
                  {(form.packaging_rows || []).length > 0 && orderQty > 0 && (
                    <Typography variant="caption" color={allocatedUnits >= orderQty ? 'success.main' : 'warning.main'} fontWeight={600}>
                      {allocatedUnits >= orderQty
                        ? `✓ ${(form.packaging_rows || []).reduce((s, r) => s + Number(r.quantity || 0), 0)} уп. (біля ТПА → заявка на склад виробництва)`
                        : `⚠ розподілено ${allocatedUnits}/${orderQty} виробів`}
                    </Typography>
                  )}
                </Box>
                {(form.packaging_rows || []).map((row, idx) => {
                  const stockItem = packStockOptions.find((x) => Number(x.id) === Number(row.stock_item))
                    || packagingStock.find((x) => Number(x.id) === Number(row.stock_item));
                  const pmKey = Number(row._pm_id || row.packaging_material || stockItem?.packaging_material);
                  const comp = (pmKey && compByPmId[pmKey]) || null;
                  const unitsPack = comp ? Number(comp.quantity) : null;
                  const available = stockItem?.quantity ?? null;
                  const enteredQty = Number(row.quantity) || 0;
                  const hasShortage = available !== null && enteredQty > 0 && enteredQty > available;
                  const noStockItem = (row.stock_item == null || row.stock_item === '') && (row._pm_id || row.packaging_material);
                  const displayName = stockItem?.packaging_material_name || row.name || 'Упаковка';
                  const selectValue = row.stock_item != null && row.stock_item !== '' ? Number(row.stock_item) : '';
                  return (
                    <Box key={idx} sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: noStockItem ? 'warning.main' : hasShortage ? 'error.main' : 'divider', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'nowrap' }}>
                        <FormControlLabel control={
                          <Checkbox size="small" checked={row.pack_at_machine !== false}
                            onChange={(e) => { const next = [...(form.packaging_rows || [])]; next[idx] = { ...next[idx], pack_at_machine: e.target.checked }; setForm((f) => ({ ...f, packaging_rows: next })); }} />
                        } label={<Typography variant="body2" noWrap>Біля ТПА</Typography>} sx={{ mr: 0, minWidth: 100, flexShrink: 0 }} />
                        {noStockItem ? (
                          <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                            <TextField size="small" label="Упаковка" value={displayName} fullWidth InputProps={{ readOnly: true }} />
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                              ⚠ «{displayName}» відсутній на складі — додайте позицію в розділі «Склади»
                            </Typography>
                          </Box>
                        ) : (
                          <TextField select size="small" label="Упаковка" value={selectValue} sx={{ flex: '1 1 180px', minWidth: 0 }}
                            onChange={(e) => {
                              const val = e.target.value !== '' ? Number(e.target.value) : '';
                              const item = packStockOptions.find((x) => Number(x.id) === Number(val))
                                || packagingStock.find((x) => Number(x.id) === Number(val));
                              const c = item ? compByPmId[Number(item.packaging_material)] : null;
                              const utp = Number(row.units_to_pack) || remainingUnits;
                              const autoQty = c && utp > 0 ? Math.ceil(utp / Number(c.quantity)) : '';
                              const next = [...(form.packaging_rows || [])];
                              next[idx] = {
                                ...next[idx],
                                stock_item: val === '' ? null : val,
                                packaging_material: item?.packaging_material ?? null,
                                _pm_id: item?.packaging_material ?? next[idx]._pm_id,
                                name: item?.packaging_material_name || '',
                                unit: item?.unit || '',
                                quantity: String(autoQty),
                              };
                              setForm((f) => ({ ...f, packaging_rows: next }));
                            }}>
                            <MenuItem value="">— обрати —</MenuItem>
                            {packStockOptions.map((x) => (
                              <MenuItem key={x.id} value={Number(x.id)}>
                                {x.packaging_material_name || 'Упаковка'}{x.packaging_material_type ? ` — ${x.packaging_material_type}` : ''} ({x.quantity != null ? `${x.quantity} ${x.unit || 'шт'} на складі` : 'немає'})
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                        {unitsPack && (
                          <TextField size="small" type="number" label="Виробів" value={row.units_to_pack ?? ''}
                            onChange={(e) => {
                              const utp = Number(e.target.value) || 0;
                              const autoQty = unitsPack && utp > 0 ? Math.ceil(utp / unitsPack) : '';
                              const next = [...(form.packaging_rows || [])];
                              next[idx] = { ...next[idx], units_to_pack: e.target.value, quantity: String(autoQty) };
                              setForm((f) => ({ ...f, packaging_rows: next }));
                            }}
                            inputProps={{ min: 1, step: 1, max: orderQty }}
                            sx={{ width: 100, flexShrink: 0 }} helperText={`${unitsPack} шт/уп.`} />
                        )}
                        <TextField size="small" type="number" label="Упаковок" value={row.quantity ?? ''}
                          onChange={(e) => { const next = [...(form.packaging_rows || [])]; next[idx] = { ...next[idx], quantity: e.target.value }; setForm((f) => ({ ...f, packaging_rows: next })); }}
                          inputProps={{ min: 0, step: 1 }} sx={{ width: 110, flexShrink: 0 }}
                          error={hasShortage} helperText={available !== null ? (hasShortage ? `Мало! є ${available}` : `є ${available}`) : ''} />
                        <IconButton size="small" color="error" sx={{ flexShrink: 0, mt: 0.5 }}
                          onClick={() => { const next = (form.packaging_rows || []).filter((_, i) => i !== idx); setForm((f) => ({ ...f, packaging_rows: next })); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
                {orderQty > 0 && allocatedUnits > 0 && allocatedUnits < orderQty && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
                    Розподілено {allocatedUnits} з {orderQty} виробів — не всі вироби мають упаковку
                  </Typography>
                )}
                <Button size="small" startIcon={<AddIcon />} sx={{ mt: 0.5 }}
                  onClick={() => {
                    const already = new Set((form.packaging_rows || []).map((r) => Number(r.stock_item)));
                    const next_stock = packStockOptions.find((s) => !already.has(Number(s.id))) || null;
                    const c = next_stock ? compByPmId[Number(next_stock.packaging_material)] : null;
                    const utp = remainingUnits > 0 ? remainingUnits : (orderQty || '');
                    const autoQty = c && Number(utp) > 0 ? Math.ceil(Number(utp) / Number(c.quantity)) : '';
                    const newRow = {
                      stock_item: next_stock ? Number(next_stock.id) : null,
                      packaging_material: next_stock ? Number(next_stock.packaging_material) : null,
                      _pm_id: next_stock ? Number(next_stock.packaging_material) : null,
                      name: next_stock?.packaging_material_name || '',
                      unit: next_stock?.unit || 'шт',
                      quantity: String(autoQty),
                      units_to_pack: utp > 0 ? String(utp) : '',
                      pack_at_machine: true,
                    };
                    setForm((f) => ({ ...f, packaging_rows: [...(f.packaging_rows || []), newRow] }));
                  }}>
                  Додати упаковку
                </Button>
              </Box>

              {errorMsg && <Typography color="error" variant="body2">{errorMsg}</Typography>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permol detail sub-dialog */}
      <Dialog open={Boolean(permolDetailOpen)} onClose={() => setPermolDetailOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Склад перемолу</DialogTitle>
        <DialogContent>
          {permolDetailOpen && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1">{permolDetailOpen.name}</Typography>
              <Typography variant="body2" color="text.secondary">{permolDetailOpen.article || '—'}</Typography>
              <Typography variant="body2" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                {permolDetailOpen.composition_description?.trim() || 'Опис складу не заповнений.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setPermolDetailOpen(null)}>Закрити</Button></DialogActions>
      </Dialog>
    </>
  );
}
